import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch, useAppSelector } from '../../../store/hooks.ts';
import {
  selectDoraFinancialEntities,
  setDoraFinancialEntities,
} from '../store/financialEntity/financialEntitySlice.ts';
import {
  selectDoraBusinessServices,
  setDoraBusinessServices,
} from '../store/businessService/businessServiceSlice.ts';
import {
  selectDoraICTServices,
  setDoraICTServices,
} from '../store/ictService/ictServiceSlice.ts';
import {
  addDoraRisk,
  removeDoraRisk,
  selectDoraRisks,
  setDoraRisks,
  updateDoraRisk as updateDoraRiskState,
} from '../store/risk/riskSlice.ts';
import {
  listDoraFinancialEntities,
  listDoraBusinessServices,
  listDoraICTServices,
  listDoraRisks,
  createDoraRisk,
  updateDoraRisk,
  deleteDoraRisk,
} from '../../../api.ts';
import { DoraRisk, doraRiskToUpdate } from '../models/doraRisk.ts';
import { selectCurrentOrganization } from '../../../store/organization/organizationSlice.ts';

const STATUS_OPTIONS = ['open', 'under_treatment', 'accepted', 'closed'];

export const RisksPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const financialEntities = useAppSelector(selectDoraFinancialEntities);
  const businessServices = useAppSelector(selectDoraBusinessServices);
  const ictServices = useAppSelector(selectDoraICTServices);
  const risks = useAppSelector(selectDoraRisks);
  const currentOrganization = useAppSelector(selectCurrentOrganization);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('open');
  const [financialEntityId, setFinancialEntityId] = useState<number | ''>('');
  const [businessServiceId, setBusinessServiceId] = useState<number | ''>('');
  const [ictServiceId, setICTServiceId] = useState<number | ''>('');

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editStatus, setEditStatus] = useState('open');
  const [editFinancialEntityId, setEditFinancialEntityId] = useState<number | ''>('');
  const [editBusinessServiceId, setEditBusinessServiceId] = useState<number | ''>('');
  const [editICTServiceId, setEditICTServiceId] = useState<number | ''>('');

  const sortedRisks = useMemo(
    () => [...risks].sort((a, b) => a.title.localeCompare(b.title)),
    [risks],
  );

  useEffect(() => {
    if (!financialEntityId && financialEntities.length > 0) {
      setFinancialEntityId(financialEntities[0].id ?? '');
    }
    if (!editFinancialEntityId && financialEntities.length > 0 && editingId) {
      setEditFinancialEntityId(financialEntities[0].id ?? '');
    }
  }, [financialEntities, financialEntityId, editFinancialEntityId, editingId]);

  const createDisabled = !title.trim() || !financialEntityId;

  const statusColor = (value: string) => {
    switch (value) {
      case 'closed':
        return 'success';
      case 'accepted':
        return 'warning';
      case 'under_treatment':
        return 'info';
      default:
        return 'default';
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!currentOrganization) return;
      setLoading(true);
      setError(null);
      try {
        const [fes, bs, icts, riskList] = await Promise.all([
          listDoraFinancialEntities(),
          listDoraBusinessServices(),
          listDoraICTServices(),
          listDoraRisks(),
        ]);
        dispatch(setDoraFinancialEntities(fes));
        dispatch(setDoraBusinessServices(bs));
        dispatch(setDoraICTServices(icts));
        dispatch(setDoraRisks(riskList));
      } catch (err: any) {
        console.error('Failed to load risks', err);
        setError(err?.response?.data?.detail || 'Failed to load risks');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentOrganization, dispatch]);

  const handleCreate = async () => {
    if (!title.trim() || !financialEntityId) {
      setError('Title and Financial Entity are required');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const created = await createDoraRisk({
        title: title.trim(),
        description: description || null,
        status,
        financial_entity_id: Number(financialEntityId),
        business_service_id: businessServiceId === '' ? null : Number(businessServiceId),
        ict_service_id: ictServiceId === '' ? null : Number(ictServiceId),
      });
      dispatch(addDoraRisk(created));
      setTitle('');
      setDescription('');
      setStatus('open');
      setFinancialEntityId('');
      setBusinessServiceId('');
      setICTServiceId('');
      setSuccess('Risk created');
    } catch (err: any) {
      console.error('Failed to create risk', err);
      setError(err?.response?.data?.detail || 'Failed to create risk');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (risk: DoraRisk) => {
    setEditingId(risk.id ?? null);
    setEditTitle(risk.title);
    setEditDescription(risk.description || '');
    setEditStatus(risk.status);
    setEditFinancialEntityId(risk.financial_entity_id);
    setEditBusinessServiceId(risk.business_service_id ?? '');
    setEditICTServiceId(risk.ict_service_id ?? '');
    setError(null);
    setSuccess(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle('');
    setEditDescription('');
    setEditStatus('open');
    setEditFinancialEntityId('');
    setEditBusinessServiceId('');
    setEditICTServiceId('');
  };

  const handleUpdate = async (risk: DoraRisk) => {
    if (!risk.id) return;
    if (!editTitle.trim() || !editFinancialEntityId) {
      setError('Title and Financial Entity are required');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = doraRiskToUpdate({
        ...risk,
        title: editTitle.trim(),
        description: editDescription || null,
        status: editStatus,
        financial_entity_id: Number(editFinancialEntityId),
        business_service_id: editBusinessServiceId === '' ? null : Number(editBusinessServiceId),
        ict_service_id: editICTServiceId === '' ? null : Number(editICTServiceId),
      });
      const updated = await updateDoraRisk(risk.id, payload);
      dispatch(updateDoraRiskState(updated));
      cancelEdit();
      setSuccess('Risk updated');
    } catch (err: any) {
      console.error('Failed to update risk', err);
      setError(err?.response?.data?.detail || 'Failed to update risk');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id?: number) => {
    if (!id) return;
    if (!window.confirm('Delete this risk?')) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await deleteDoraRisk(id);
      dispatch(removeDoraRisk(id));
      setSuccess('Risk deleted');
    } catch (err: any) {
      console.error('Failed to delete risk', err);
      setError(err?.response?.data?.detail || 'Failed to delete risk');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Add Risk
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="risk-status-label">Status</InputLabel>
              <Select
                labelId="risk-status-label"
                value={status}
                label="Status"
                onChange={(e: SelectChangeEvent<string>) => setStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            sx={{ mt: 2 }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="risk-fe-label">Financial Entity</InputLabel>
              <Select
                labelId="risk-fe-label"
                value={financialEntityId === '' ? '' : String(financialEntityId)}
                label="Financial Entity"
                onChange={(e: SelectChangeEvent<string>) => setFinancialEntityId(Number(e.target.value))}
              >
                {financialEntities.map(fe => (
                  <MenuItem key={fe.id} value={fe.id}>{fe.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="risk-bs-label" shrink>Business Service</InputLabel>
              <Select
                labelId="risk-bs-label"
                value={businessServiceId === '' ? '' : String(businessServiceId)}
                label="Business Service"
                onChange={(e: SelectChangeEvent<string>) => setBusinessServiceId(e.target.value ? Number(e.target.value) : '')}
                displayEmpty
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {businessServices.map(bs => (
                  <MenuItem key={bs.id} value={bs.id}>{bs.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="risk-ict-label" shrink>ICT Service</InputLabel>
              <Select
                labelId="risk-ict-label"
                value={ictServiceId === '' ? '' : String(ictServiceId)}
                label="ICT Service"
                onChange={(e: SelectChangeEvent<string>) => setICTServiceId(e.target.value ? Number(e.target.value) : '')}
                displayEmpty
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {ictServices.map(ict => (
                  <MenuItem key={ict.id} value={ict.id}>{ict.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" onClick={handleCreate} disabled={loading || createDisabled}>
              Create
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      {financialEntities.length === 0 && (
        <Alert severity="warning">
          No Financial Entities found. Please create financial entities in the <strong>Financial Entities</strong> panel (B_01.02) before creating risks.
        </Alert>
      )}

      {(businessServices.length === 0 || ictServices.length === 0) && (
        <Alert severity="info">
          Missing optional data for linking risks:{' '}
          {businessServices.length === 0 && <><strong>Business Services</strong> (Business Services panel){businessServices.length === 0 && ictServices.length === 0 && ', '}</>}
          {ictServices.length === 0 && <><strong>ICT Services</strong> (ICT Services panel)</>}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6">Risks</Typography>
            {loading && <CircularProgress size={20} />}
          </Stack>
          {sortedRisks.length === 0 ? (
            <Alert severity="info">No risks yet. Add one above.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Financial Entity</TableCell>
                    <TableCell>Business Service</TableCell>
                    <TableCell>ICT Service</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRisks.map(risk => {
                    const isEditing = editingId === risk.id;
                    const feName = financialEntities.find(fe => fe.id === risk.financial_entity_id)?.name || '—';
                    const bsName = risk.business_service_id
                      ? (businessServices.find(bs => bs.id === risk.business_service_id)?.name || '—')
                      : 'None';
                    const ictName = risk.ict_service_id
                      ? (ictServices.find(ict => ict.id === risk.ict_service_id)?.name || '—')
                      : 'None';
                    return (
                      <TableRow key={risk.id ?? risk.title}>
                        <TableCell width="20%">
                          {isEditing ? (
                            <TextField value={editTitle} onChange={(e) => setEditTitle(e.target.value)} size="small" fullWidth />
                          ) : risk.title}
                        </TableCell>
                        <TableCell width="14%">
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={editStatus}
                                onChange={(e: SelectChangeEvent<string>) => setEditStatus(e.target.value)}
                              >
                                {STATUS_OPTIONS.map(option => (
                                  <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <Chip label={risk.status} size="small" color={statusColor(risk.status) as any} />
                          )}
                        </TableCell>
                        <TableCell width="18%">
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={editFinancialEntityId === '' ? '' : String(editFinancialEntityId)}
                                onChange={(e: SelectChangeEvent<string>) => setEditFinancialEntityId(Number(e.target.value))}
                              >
                                {financialEntities.map(fe => (
                                  <MenuItem key={fe.id} value={fe.id}>{fe.name}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : feName}
                        </TableCell>
                        <TableCell width="18%">
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={editBusinessServiceId === '' ? '' : String(editBusinessServiceId)}
                                onChange={(e: SelectChangeEvent<string>) => setEditBusinessServiceId(e.target.value ? Number(e.target.value) : '')}
                                displayEmpty
                              >
                                <MenuItem value="">
                                  <em>None</em>
                                </MenuItem>
                                {businessServices.map(bs => (
                                  <MenuItem key={bs.id} value={bs.id}>{bs.name}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : bsName}
                        </TableCell>
                        <TableCell width="18%">
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={editICTServiceId === '' ? '' : String(editICTServiceId)}
                                onChange={(e: SelectChangeEvent<string>) => setEditICTServiceId(e.target.value ? Number(e.target.value) : '')}
                                displayEmpty
                              >
                                <MenuItem value="">
                                  <em>None</em>
                                </MenuItem>
                                {ictServices.map(ict => (
                                  <MenuItem key={ict.id} value={ict.id}>{ict.name}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : ictName}
                        </TableCell>
                        <TableCell align="right">
                          {isEditing ? (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <IconButton onClick={() => handleUpdate(risk)} size="small" disabled={loading}>
                                <SaveIcon fontSize="small" />
                              </IconButton>
                              <IconButton onClick={cancelEdit} size="small" disabled={loading}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          ) : (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <IconButton onClick={() => startEdit(risk)} size="small" disabled={loading}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton onClick={() => handleDelete(risk.id)} size="small" disabled={loading}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};

export default RisksPanel;
